// @ts-nocheck
import { Angulartics2 } from 'angulartics2';
import { Angulartics2Facebook } from 'angulartics2/facebook';
import { Angulartics2GoogleAnalytics } from 'angulartics2/ga';
import {
  get, identity, isEqual, isObject, merge, pickBy, transform,
} from 'lodash-es';
import {
  EventEmitter, Inject, Injectable, Output, PLATFORM_ID,
} from '@angular/core';
import { Subscription, throwError } from 'rxjs';
import { catchError, map, startWith } from 'rxjs/operators';
import { isPlatformBrowser } from '@angular/common';
import mixpanel from 'mixpanel-browser';
import { RestApiService } from './rest-api.service';
// import { v4 as uuidv4 } from 'uuid';
import { P } from '../ui-kit';

export interface AnalyticsEvent {
  data: {
    event: string;
    action?: string;
    label?: string;
    properties?: {
      value?: string;
      category?: string;
      route?: string;
    };
    custom?: P.Dictionary<string | number | boolean>;
    sync?: P.AnalyticsUserSync,
    goal?: boolean
  };
  options?: any;
}

@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  @Output() event: EventEmitter<{ event: AnalyticsEvent, sync?: P.AnalyticsUserSync }> = new EventEmitter();

  // events queue
  private queue: AnalyticsEvent[] = [];

  // mixpanel user store
  private syncStore: P.Dictionary<P.AnalyticsUserSync> = {};

  // persistence id
  get proportunityPersistenceId(): string {
    //
    const proportunityPersistenceId = this.origin?.persistence_id
      ? this.origin.persistence_id
      : this.generateProportunityPersistenceId();
    //
    return proportunityPersistenceId;
  }

  // mixpanel distinct id
  get mixpanelDistinctId(): string {
    return mixpanel?.persistence?.props?.distinct_id;
  }

  // user store
  private user: P.User<P.Applicant | P.Advisor | P.Admin>;

  // orgin store
  // tslint:disable-next-line: variable-name
  private _origin = {};

  get origin(): { [key: string]: string } {
    return this._origin;
  }

  set origin(origin: { [key: string]: string }) {
    const current = this.origin;
    const origins = merge(current, origin);
    // tslint:disable-next-line: variable-name
    const _origin = {
      ...origins,
      persistence_id: origins?.persistence_id
        ? origins.persistence_id
        : this.proportunityPersistenceId,
    };
    this._origin = _origin;
  }

  //
  private subscription: { [key: string]: Subscription } = {};

  private isBrowser: boolean;

  constructor(
    @Inject(PLATFORM_ID) platformId,
    @Inject('config') private config,
    private restApi: RestApiService,
    private angulartics2: Angulartics2,
    private angulartics2GoogleAnalytics: Angulartics2GoogleAnalytics,
    private angulartics2Facebook: Angulartics2Facebook,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (!this.isBrowser) { return; }
    // start tracking
    this.startTracking();
    // identify on auth events
    this.restApi.identity
      .pipe(
        startWith(this.restApi.accreditation),
        map((accreditation) => accreditation?.user),
      )
      .subscribe(this.identify.bind(this));
  }

  /**
   * init google and fb analytics
   */
  public startTracking(): void {
    // google analytics
    this.angulartics2GoogleAnalytics.startTracking();
    // facebook
    this.angulartics2Facebook.startTracking();
    // mixpanel
    mixpanel.init(this.config.environment === 'production'
      ? 'c895b38be7c24fb06ff999984b795510'
      : '0f6fd757ea2423e3592910447de08260');
  }

  /**
   * track event
   */
  public eventTrack(event: AnalyticsEvent, user: P.User<P.Applicant | P.Advisor | P.Admin | P.LeadUser> = this.user): void {
    // track event in proportunity
    this.sendEvent(event, user);
    // if queueEvents enabled
    if (this.config.environment.queueEvents) {
      while (this.queue.length > 0) {
        const pastEvent = this.queue.shift();
        this.sendEvent(pastEvent, user);
      }
    }
  }

  /**
   *
   */
  public sendEvent(event: AnalyticsEvent, user?: P.User<P.Applicant | P.Advisor | P.Admin | P.LeadUser>): void {
    // dont sync if no persistence_id
    const sync = user?.persistence_id
      ? this.getUserSync(user)
      : null;
    // propagate to google analytics
    this.sendEventToGAnalytics(event);
    // propagate to mixpanel
    this.sendEventToMixpanel(event, sync);
    //
    this.event.emit({ event, sync });
  }

  /**
   *
   */
  private sendEventToGAnalytics(event: AnalyticsEvent): void {
    //
    delete event.data.event;
    //
    this.angulartics2.eventTrack.next(event.data);
  }

  /**
   * on tracking success handler
   * @param response tracking success response
   */
  private onSuccess() {
    // SUCCESS. NOTHING TO DO
  }

  /**
   * identify user
   */
  public identify(user: P.User<P.Applicant | P.Advisor | P.Admin> = this.restApi.accreditation?.user): void {
    // if user
    if (user) {
      const sync = this.getUserSync(user);
      //
      this.identifyInMixpanel(sync);
    } else {
      // reset if no user
      // mixpanel.reset();
      // delete authenticated user data store
      for (const t of Object.keys(this.syncStore)) {
        if (t !== 'Unauthenticated') {
          delete this.syncStore[t];
        }
      }
    }
    //
    this.user = user;
  }

  /**
   *
   */
  public getUserSync(user: P.User<P.Applicant | P.Advisor | P.Admin | P.LeadUser>, deltaOnly: boolean = false): P.AnalyticsUserSync {
    // get or generate user's persistence id
    const persistenceId = user?.persistence_id || this.proportunityPersistenceId;
    // user type
    const userType = user?.type || 'Unauthenticated';
    // init sync object
    const sync: P.AnalyticsUserSync = {
      identity: persistenceId,
      type: userType,
      application: this.config.application,
    };
    // if user
    if (user) {
      // user id
      const userId = user?.id;
      //
      const common = {
        /**
         * User
         */
        User: userId,
        $name: [user?.first_name, user?.last_name].filter((v) => !!v).join(' '),
        $email: user?.email,
        Phone: (user as any)?.phone,
        Verified: (user as any)?.verified,
      };
      // add user id as alias
      sync.alias = [userId].filter((v) => !!v);
      //
      switch (user.type) {
        case 'Advisor':
          sync.data = pickBy({
            ...common,
            /**
             * Broker
             */
            Broker: (user.broker as P.Broker)?.name,
            'Broker Id': (user.broker as P.Broker)?.id,
          }, identity);
          break;
        case 'Admin':
          sync.data = pickBy({
            ...common,

          }, identity);
          break;
        case 'Applicant':
        case 'Lead':
        default:
          // application data
          const application = (user as P.User<P.Applicant>)?.application || {};
          // application id
          const applicationId = application.id;
          // add application id as alias
          sync.alias = [...sync.alias, user?.email].filter((v) => !!v);
          // create sync data
          sync.data = pickBy({
            ...common,
            /**
             * Account
             */
            Account: applicationId,
            'Is Joint Account': application?.joint_application,
            'Account Created By': (application?.createdBy as any)?.id || application?.createdBy,
            'Account Created By Type': (application?.createdBy as any)?.type,
            'Account Created At': application?.createdAt,
            'Account Updated At': application?.updatedAt,
            'Account Has Clean Credit Record': application?.has_clean_credit_record,
            'Account Has Steady Income Source': application?.has_steady_income_source,
            'Account Applicants Within Age Range': application?.is_within_age_range,
            'Account # Shortlisted Homes': (application?.shortlist || []).length,
            'Qualifier Date': application?.qualifier?.createdAt,
            'Qualifier Timeline': application?.qualifier?.timeline?.answer,
            'Qualifier Deposit': application?.qualifier?.deposit?.answer,
            'Qualifier Home': application?.qualifier?.home?.answer,
            Income: application?.income,
            'Income Extra': application?.income_extra,
            Deposit: application?.deposit,
            Budget: application?.confirmedBudget || application?.property_price,
            'Broker Id': application?.broker?.id,
            'Broker Referred': application?.referred_to_broker,
            'Broker Referred At': application?.referredAt,
            'Broker Referred Self': application?.referredSelf,
            Broker: application?.broker?.name,
            'Loan Application Id': !!application?.loan?.id,
            'Loan Application Open': !!application?.loan,
            'Loan Application Open At': application?.loan?.createdAt,
            'Loan Application Completed': application?.loan?.completed,
            'Loan Application Completed At': application?.loan?.completedDate,
            'Residence Type': application?.applicants[0]?.residence_type,
            Segment: application?.qualifier?.segment,
            'Broker Assessment': application?.broker_assessment?.statusId,

            /**
             * Origin
             */
            ...(this.origin || {}),
            /**
             * origin:
             * - gclid
             * - persistence_id
             * - autopilot_session_id
             * - referrer
             * - utm_* (any utms)
             */
          }, identity);
          break;
      }
    }
    // sync delta changes only
    if (deltaOnly) {
      // get synced data store
      const syncStore: any = (this.syncStore[sync.type] || {});
      //
      const syncDelta = this.difference(sync.data, syncStore.data);
      // update synced data store
      this.syncStore[sync.type] = sync;
      // sync data delta
      sync.data = syncDelta;
    } else {
      // update synced data store
      this.syncStore[sync.type] = sync;
    }
    //
    return sync;
  }

  /**
   *
   */
  public getEventName(event: AnalyticsEvent): string {
    return [
      event.data?.action,
      event.data?.event,
      event.data?.label,
    ]
      .filter((item) => !!item)
      .join('-');
  }

  /**
   * identify user in mixpanel
   */
  private identifyInMixpanel(sync: P.AnalyticsUserSync): void {
    // identify mixpanel user
    mixpanel.identify(sync.identity);
    // add to group
    mixpanel.add_group('type', sync.type);
    //
    mixpanel.add_group('platform', sync.application);
    // identify aliases (i.e. user id)
    for (const alias of sync.alias || []) {
      mixpanel.alias(sync.identity, alias);
    }
    // if user data
    if (sync?.data) {
      mixpanel.people.set(sync.data);
    }
  }

  /**
   * send event to mixpanel
   */
  private sendEventToMixpanel(event: AnalyticsEvent, sync?: P.AnalyticsUserSync): void {
    // identify user (delta only sync)
    if (sync) {
      this.identifyInMixpanel(sync);
    } else {
      mixpanel.identify(this.proportunityPersistenceId);
    }
    // parse event data
    const data = {
      event: event.data.event,
      action: event.data.action,
      label: event.data.label,
      ...(event.data.properties || {}),
      ...(event.data.custom || {}),
      ...(this.origin || {}),
      platform: this.config.application,
    };
    // build event name
    const eventName = this.getEventName(event);
    // track event
    mixpanel.track(eventName, data);
  }

  /**
   * generate prop persistence id
   */
  private generateProportunityPersistenceId(): string {
    return mixpanel.get_distinct_id();
    // return uuidv4();
  }

  /**
   *
   */
  private difference(object: any, base: any): any {
    // tslint:disable-next-line: variable-name
    const changes = (_object: any, _base: any) => transform(_object, (result, value, key) => {
      if (!isEqual(value, _base[key])) {
        result[key] = (isObject(value) && isObject(_base[key]))
          ? changes(value, _base[key])
          : value;
      }
    });
    return changes(object, base);
  }
}
